import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';
import { ctaPrimary, ctaSecondary } from '../../shared-theme/brand';

// Calendly URL with theming parameters to match brand colors
const CALENDLY_URL = 'https://calendly.com/d/cr7w-cgy-752/open-intake-ai-introduction?' + 
  'background_color=fdfcf9&' +  // Soft Ivory (lighter website background)
  'text_color=3e301e&' +         // Dark Brown text
  'primary_color=b5683c&' +      // Copper/Orange accent
  'hide_gdpr_banner=1';          // Hide cookie banner

// Inline waveform animation for the play button
const waveformBars = [
  { height: 0.3, delay: 0 },
  { height: 0.5, delay: 0.1 },
  { height: 0.8, delay: 0.2 },
  { height: 0.4, delay: 0.15 },
  { height: 0.9, delay: 0.25 },
  { height: 0.6, delay: 0.05 },
  { height: 0.7, delay: 0.3 },
  { height: 0.5, delay: 0.12 },
  { height: 0.85, delay: 0.22 },
  { height: 0.4, delay: 0.08 },
  { height: 0.6, delay: 0.18 },
  { height: 0.75, delay: 0.28 },
];

function InlineWaveform() {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2.5px',
        overflow: 'hidden',
        borderRadius: '50px',
        zIndex: 0,
        px: 2,
        '@keyframes soundwave': {
          '0%, 100%': {
            transform: 'scaleY(0.3)',
          },
          '50%': {
            transform: 'scaleY(1)',
          },
        },
      }}
    >
      {waveformBars.map((bar, i) => (
        <Box
          key={i}
          sx={{
            width: '3px',
            height: `${bar.height * 100}%`,
            maxHeight: '70%',
            minHeight: '15%',
            background: 'linear-gradient(180deg, rgba(181, 104, 60, 0.5) 0%, rgba(181, 104, 60, 0.2) 100%)',
            borderRadius: '3px',
            animation: `soundwave ${0.6 + bar.delay}s ease-in-out ${bar.delay}s infinite`,
          }}
        />
      ))}
    </Box>
  );
}

export default function BookingSection() {
  const [showCalendly, setShowCalendly] = React.useState(false);
  const [showMobileModal, setShowMobileModal] = React.useState(false);
  const [isCalendlyLoaded, setIsCalendlyLoaded] = React.useState(false);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  // Detect mobile
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 900);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Listen for custom event to open Calendly
  React.useEffect(() => {
    const handleOpenCalendly = () => {
      if (window.innerWidth < 900) {
        setShowMobileModal(true);
      } else {
        setShowCalendly(true);
      }
    };
    window.addEventListener('openCalendly', handleOpenCalendly);
    return () => window.removeEventListener('openCalendly', handleOpenCalendly);
  }, []);

  React.useEffect(() => {
    // Load Calendly script if not already loaded
    if (!document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
    }

    // Listen for Calendly events
    const handleCalendlyEvent = (e: MessageEvent) => {
      if (e.data.event && e.data.event.indexOf('calendly') === 0) {
        if (e.data.event === 'calendly.page_height' || e.data.event === 'calendly.event_type_viewed') {
          setIsCalendlyLoaded(true);
        }
      }
    };

    window.addEventListener('message', handleCalendlyEvent);
    return () => window.removeEventListener('message', handleCalendlyEvent);
  }, []);

  // Initialize Calendly widget when showCalendly becomes true
  React.useEffect(() => {
    if (showCalendly && (window as any).Calendly) {
      setIsCalendlyLoaded(false);
      (window as any).Calendly.initInlineWidget({
        url: CALENDLY_URL,
        parentElement: document.querySelector('.calendly-inline-widget'),
      });
    }
  }, [showCalendly]);

  const handleListenExample = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio('/website_demo_pi.mp3');
      audioRef.current.addEventListener('ended', () => {
        setIsPlaying(false);
      });
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  // Cleanup audio on unmount
  React.useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <Box
      id="booking"
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            backgroundColor: '#F7F3EB', // Light Tan
            borderRadius: 4,
            py: { xs: 6, md: 8 },
            px: { xs: 3, md: 6 },
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h2"
            sx={{
              mb: 2,
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 500,
              letterSpacing: '-0.025em',
              color: '#3E301E',
            }}
          >
            See it answer your firm's calls
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: '#6B5F4D',
              maxWidth: '550px',
              mx: 'auto',
              mb: 4,
              fontSize: { xs: '1rem', md: '1.125rem' },
              lineHeight: 1.7,
            }}
          >
            Book a 20-minute intro and we'll walk through a live intake call,
            tailored to your practice areas.
          </Typography>

          {/* CTA Buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            sx={{ mb: showCalendly ? 6 : 0 }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => isMobile ? setShowMobileModal(true) : setShowCalendly(true)}
              sx={ctaPrimary}
            >
              Pick a time
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={isPlaying ? <PauseIcon sx={{ zIndex: 1 }} /> : <PlayArrowIcon sx={{ zIndex: 1 }} />}
              onClick={handleListenExample}
              sx={{
                ...ctaSecondary,
                position: 'relative',
                overflow: 'hidden',
                '& .MuiButton-startIcon': { zIndex: 1, color: '#B5683C' },
              }}
            >
              {isPlaying && <InlineWaveform />}
              <Box component="span" sx={{ zIndex: 1, position: 'relative' }}>
                {isPlaying ? 'Pause' : 'Hear a sample call'}
              </Box>
            </Button>
          </Stack>

          {/* Calendly Widget - Desktop Only */}
          {showCalendly && !isMobile && (
            <Box
              sx={{
                width: 'calc(100% + 48px)',
                mx: { xs: -3, md: -6 },
                mt: 4,
                position: 'relative',
              }}
            >
              {/* Loading State */}
              {!isCalendlyLoaded && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#F7F3EB',
                    zIndex: 1,
                    gap: 2,
                  }}
                >
                  <CircularProgress 
                    sx={{ 
                      color: '#B5683C',
                    }} 
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: '#6B5F4D' }}
                  >
                    Loading calendar…
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  height: '750px',
                  opacity: isCalendlyLoaded ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
              >
                <div 
                  className="calendly-inline-widget" 
                  data-url={CALENDLY_URL}
                  style={{ 
                    minWidth: '320px', 
                    height: '100%',
                    width: '100%'
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Container>

      {/* Mobile Calendly Modal */}
      {showMobileModal && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: '#FDFCF9',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header with close button */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: '1px solid #E8E2D8',
              backgroundColor: '#FDFCF9',
            }}
          >
            <Typography sx={{ fontWeight: 600, color: '#3E301E' }}>
              Book a demo
            </Typography>
            <IconButton
              onClick={() => setShowMobileModal(false)}
              sx={{
                color: '#6B5F4D',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Calendly iframe */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <iframe
              src={CALENDLY_URL}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title="Book a demo"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
